import { AI_BUDDIES } from '../../constants';
import agentApi from '../agent/agentApi';
import type { AIBuddy, FeedPost } from '../../types';

import { getFullImageUrl } from '../../utils/imageUrl';
import { getTimezoneOffsetMinutes } from '../../utils/dateHelpers';
import { getApiErrorMessage } from '../../utils/apiErrorHandler';

export interface SuggestCommentResult {
  success: boolean;
  message?: string;
  postCommentId?: number;
  commentText?: string;
}

export interface AiBuddySettings {
  keepHistoryFor30Days: boolean;
  enableSuggestLikes: boolean;
  enableSuggestComments: boolean;
}

export interface AiBuddySettingsPayload {
  agentUserId: number;
  keepHistoryFor30Days: boolean;
  enableSuggestLikes: boolean;
  enableSuggestComments: boolean;
}

// Simulate network delay
const delay = (ms = 500): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

export interface GetAIBuddiesParams {
  pageNumber?: number;
  pageSize?: number;
  timezoneOffset?: number;
  isActive?: boolean;
}

export interface GetAIBuddiesResponse {
  data: AIBuddy[];
  pageNumber: number;
  pageSize: number;
  totalRecords?: number;
  totalPages?: number;
  hasMore: boolean;
}

const getAIBuddies = async (params?: GetAIBuddiesParams): Promise<GetAIBuddiesResponse> => {
  const pageNo = params?.pageNumber ?? 1;
  const pageSize = params?.pageSize ?? 20;
  const timezoneOffset = params?.timezoneOffset ?? getTimezoneOffsetMinutes();
  const isActive = params?.isActive ?? true;

  const response = await agentApi.agentsList({
    timezoneOffset,
    isActive,
    pageNumber: pageNo,
    pageSize,
  });

  const rawData = response.data;
  const rawAgents = Array.isArray(rawData?.agents)
    ? rawData.agents
    : Array.isArray(rawData?.data?.agents)
      ? rawData.data.agents
      : Array.isArray(rawData?.data)
        ? rawData.data
        : [];

  const agents: AIBuddy[] = rawAgents.map((agent: any) => ({
    id: (agent.agentId ?? agent.id ?? '').toString(),
    name: agent.userName || agent.name || 'AI Buddy',
    role: agent.motivationStyle || agent.role || 'AI Buddy',
    description: agent.bio || agent.description || agent.tone || '',
    about: agent.bio || agent.about || '',
    profileUserGuid: agent.profileUserGuid || agent.agentGuid || agent.userGuid || '',
    image: getFullImageUrl(agent.agentPhoto || agent.image || agent.profilePictureUrl),
    age: 0,
    country: '',
    goalLbs: 0,
    specialties: [agent.motivationStyle, agent.tone].filter(Boolean),
    isFollowing: agent.isFollowing === true || agent.isFollowing === 1,
  }));

  const totalRecords = rawData?.totalRecords ?? rawData?.totalCount ?? rawData?.total ?? rawData?.data?.totalRecords;
  const totalPages = rawData?.totalPages ?? rawData?.data?.totalPages;

  let hasMore = false;
  if (typeof totalPages === 'number') {
    hasMore = pageNo < totalPages;
  } else if (typeof totalRecords === 'number') {
    hasMore = pageNo * pageSize < totalRecords;
  } else if (typeof rawData?.hasNextPage === 'boolean') {
    hasMore = rawData.hasNextPage;
  } else if (typeof rawData?.hasMore === 'boolean') {
    hasMore = rawData.hasMore;
  } else {
    hasMore = agents.length >= pageSize && agents.length > 0;
  }

  return {
    data: agents,
    pageNumber: pageNo,
    pageSize,
    totalRecords,
    totalPages,
    hasMore,
  };
};

const getBuddyById = async (id: string) => {
  await delay();
  const buddy = AI_BUDDIES.find(b => b.id === id);
  if (!buddy) {
    throw new Error('Buddy not found');
  }
  return { data: buddy };
};

/**
 * `template` carries the placeholder posts until the feed endpoint exists; it is
 * supplied by the caller because the fixtures reference bundled UI assets.
 */
const getBuddyFeed = async (buddyId: string, template: FeedPost[] = []) => {
  await delay();
  const buddy = AI_BUDDIES.find(b => b.id === buddyId);
  if (!buddy) {
    throw new Error('Buddy not found');
  }
  const feed = template.map(post => ({
    ...post,
    username: buddy.name,
    avatar: buddy.image,
    userId: buddy.id
  }));
  return { data: feed };
};

const getAiBuddySettings = async (agentUserId: number): Promise<{ data: AiBuddySettings | null }> => {
  try {
    const response = await agentApi.getAiBuddySettings(agentUserId);
    const resData = response.data?.data;
    if (!resData) {
      return { data: null };
    }
    return {
      data: {
        keepHistoryFor30Days: !!resData.keepHistoryFor30Days,
        enableSuggestLikes: !!resData.enableSuggestLikes,
        enableSuggestComments: !!resData.enableSuggestComments,
      },
    };
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || 'Failed to fetch AI Settings';
    throw new Error(message);
  }
};

const saveAiBuddySettings = async (payload: AiBuddySettingsPayload): Promise<{ success: boolean }> => {
  try {
    await agentApi.saveAiBuddySettings(payload);
    return { success: true };
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || 'Failed to save settings';
    throw new Error(message);
  }
};

const suggestComment = async (
  postId: number,
  agentUserId: number,
): Promise<SuggestCommentResult> => {
  try {
    const response = await agentApi.suggestComment({ postId, agentUserId });
    const data = response.data;
    const isSuccess =
      (response.status === 201 || response.status === 200) &&
      data?.success !== false &&
      typeof data?.postCommentId === 'number' &&
      typeof data?.commentText === 'string';

    if (isSuccess) {
      return {
        success: true,
        message: data.message,
        postCommentId: data.postCommentId,
        commentText: data.commentText,
      };
    }

    return {
      success: false,
      message: data?.message || 'Failed to suggest comment',
    };
  } catch (err: unknown) {
    return {
      success: false,
      message: getApiErrorMessage(err as import('../../types/api').ApiError),
    };
  }
};

const aiBuddyService = {
  getAIBuddies,
  getBuddyById,
  getBuddyFeed,
  getAiBuddySettings,
  saveAiBuddySettings,
  suggestComment,
};

export default aiBuddyService;
